import { useState } from "react";
import { postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import {
  Counter,
  DataTable,
  FilterRow,
  MobileActions,
  Pill,
  RowsSkeleton,
  useFilters,
  useIsMobile,
} from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts } from "../texts";
import { Notice, PageHead, useToast } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState } from "../ui/pieces";
import {
  approvalPillKey,
  approvalStatusKey,
  approvalStatuses,
  approvalsCounter,
  argumentLabel,
  intentKey,
  policyRank,
  policyText,
  type OrgHead,
} from "./employees";
import { EmployeeAvatar, EmployeesHead, Failed, replyMessage } from "./EmployeesPages";
import { whenIn } from "./organizations";
import { ConfirmWindow } from "./PersonActions";
import { Facts } from "./SecurityBlocks";
import { SimpleTable } from "./SimpleTable";
import own from "./employees.module.css";

// R5 · Empresa › Empleados IA › Aprobaciones y Políticas de aprobación (§5.6). B3: la cola se pagina en Platform con su
// total (antes se cortaba en 50 sin decirlo); el contador y el paginado salen del mismo total (E100).

type ApprovalRow = {
  id: number;
  action: string;
  employee: string;
  intent: string;
  status: string;
  createdAt: string;
};

/** Estado de una solicitud: Pendiente en ocre (C15); las demás en texto. */
function StatusTag({ status }: { status: string }) {
  const { L } = useTexts();
  const text = L(approvalStatusKey[status] ?? "ADM_APR_STATUS_PENDING");
  return status === "PENDING" ? <span className="tm-tag ochre">{text}</span> : <>{text}</>;
}

/**
 * `emp-aprobaciones` · píldoras de estado (Pendientes por defecto) en la URL, tabla C28 Acción · Empleado IA · Intención ·
 * Estado · Creada (C17 en el celular), paginado C28. Clic en la fila abre la solicitud. Si la persona no puede decidir,
 * el aviso lo dice (C41).
 */
export function ApprovalsPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const mobile = useIsMobile();
  const filters = useFilters();
  const status = (approvalStatuses as readonly string[]).includes(filters.get("status"))
    ? filters.get("status")
    : "PENDING";
  const list = useServerList<ApprovalRow>(`/api/admin/organizations/${orgId}/approvals?status=${status}`);
  const org = (list.extra?.organization as OrgHead | undefined) ?? null;
  const canDecide = list.extra?.canDecide as boolean | undefined;
  useOpenOrg(org);
  useTitle(`${L("ADM_ORG_EMPLOYEES")} · ${L("ADM_EMP_TAB_APPROVALS")}`);
  if (list.state === "notFound") return <OrgNotFound />;
  const head = <EmployeesHead orgId={orgId} current="approvals" />;
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;
  const zone = org?.timezone ?? "America/Lima";
  const href = (row: ApprovalRow) => `/Organizations/Aprobacion?id=${orgId}&approvalId=${row.id}`;
  return (
    <>
      {head}
      <FilterRow label={L("ADM_APR_FILTER")}>
        {approvalStatuses.map((value) => (
          <Pill
            key={value}
            pressed={status === value}
            onClick={() => filters.set({ status: value === "PENDING" ? null : value })}
          >
            {L(approvalPillKey[value]!)}
          </Pill>
        ))}
      </FilterRow>
      {canDecide === false ? <Notice tone="info">{L("ADM_APR_OWN_SCOPE")}</Notice> : null}
      {list.state === "loading" ? (
        <RowsSkeleton rows={5} table={!mobile} />
      ) : list.state === "error" ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      ) : list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L(status === "PENDING" ? "ADM_APR_EMPTY_PENDING" : "ADM_APR_EMPTY")}</strong>
          <span>{L(status === "PENDING" ? "ADM_APR_EMPTY_PENDING_LINE" : "ADM_APR_EMPTY_LINE")}</span>
          {status === "ALL" ? null : (
            <button type="button" className="tm-btn secondary" onClick={() => filters.set({ status: "ALL" })}>
              {L("ADM_APR_SEE_ALL")}
            </button>
          )}
        </div>
      ) : (
        <>
          <Counter>{approvalsCounter(L, locale, status, list.total)}</Counter>
          <div className={own.fit}>
            <DataTable
              label={L("ADM_EMP_TAB_APPROVALS")}
              rows={list.items}
              rowKey={(row) => row.id}
              href={href}
              columns={[
                { key: "action", title: L("ADM_EMP_COL_ACTION"), render: (row) => <strong>{row.action}</strong> },
                {
                  key: "employee",
                  title: L("ADM_EMP_COL_EMPLOYEE"),
                  render: (row) => (
                    <span className={own.nameCell} title={row.employee}>
                      <EmployeeAvatar />
                      <span>{row.employee}</span>
                    </span>
                  ),
                },
                { key: "intent", title: L("ADM_APR_COL_INTENT"), render: (row) => L(intentKey(row.intent)) },
                { key: "status", title: L("ADM_EMP_COL_STATUS"), render: (row) => <StatusTag status={row.status} /> },
                {
                  key: "created",
                  title: L("ADM_APR_COL_CREATED"),
                  render: (row) => whenIn(L, locale, row.createdAt, zone),
                },
              ]}
              mobile={(row) => ({
                title: row.action,
                tag: row.status === "PENDING" ? <StatusTag status={row.status} /> : undefined,
                line: [
                  row.employee,
                  L(intentKey(row.intent)),
                  row.status === "PENDING" ? null : L(approvalStatusKey[row.status] ?? ""),
                  whenIn(L, locale, row.createdAt, zone),
                ]
                  .filter(Boolean)
                  .join(" · "),
              })}
            />
          </div>
          <ListFooter list={list} />
        </>
      )}
    </>
  );
}

type ApprovalRecord = {
  organization: OrgHead;
  approval: {
    id: number;
    action: string;
    status: string;
    employee: string;
    job: string | null;
    intent: string;
    policy: string;
    createdAt: string;
    decidedAt: string | null;
    decidedBy: string | null;
    decidedByKind: string | null;
    execution: string | null;
  };
  arguments: { key: string; value: string }[];
  canDecide: boolean;
  canDecideAny: boolean;
};

/** Resultado de la solicitud en palabras del negocio (nunca el código de la ejecución). */
function resultText(L: (key: string) => string, record: ApprovalRecord["approval"]): string {
  if (record.status === "PENDING") return L("ADM_APR_RESULT_WAITING");
  if (record.status === "EXPIRED") return L("ADM_APR_RESULT_EXPIRED");
  if (record.status === "DENIED") return L("ADM_APR_RESULT_DENIED");
  switch (record.execution) {
    case "SUCCESS":
    case "SIMULATED_SUCCESS":
      return L("ADM_APR_RESULT_DONE");
    case "ERROR":
      return L("ADM_APR_RESULT_FAILED");
    default:
      return L("ADM_APR_RESULT_RUNNING");
  }
}

/**
 * `emp-aprobacion` · ← a Aprobaciones, la acción con su estado, «{empleado} · creada {cuándo}»; Aprobar y Rechazar
 * confirman (D8). Datos de la solicitud y «Lo que va a hacer» con los argumentos como datos legibles (D7: sin JSON ni
 * códigos). Una solicitud ya decidida muestra la decisión, quién y cuándo, sin botones.
 */
export function ApprovalPage({ orgId, approvalId }: { orgId: number; approvalId: number }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const data = useAiData<ApprovalRecord>(`/api/admin/organizations/${orgId}/approvals/${approvalId}`);
  const [deciding, setDeciding] = useState<"APPROVED" | "DENIED" | null>(null);
  useOpenOrg(data.data?.organization ?? null);
  const record = data.data;
  useTitle(record?.approval.action ?? L("ADM_EMP_TAB_APPROVALS"));
  const back = { href: `/Organizations/Aprobaciones?id=${orgId}`, label: L("ADM_EMP_TAB_APPROVALS") };
  if (data.state === "missing")
    return (
      <PageState
        head={<PageHead title={L("ADM_EMP_TAB_APPROVALS")} back={back} />}
        title={L("ADM_APR_MISSING")}
        line={L("ADM_APR_MISSING_LINE")}
        action={
          <a className="tm-btn secondary" href={back.href}>
            {L("ADM_APR_BACK")}
          </a>
        }
      />
    );
  if (!record) {
    const head = <PageHead title={L("ADM_EMP_TAB_APPROVALS")} back={back} />;
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        {head}
        <RowsSkeleton rows={3} table={!mobile} />
      </>
    );
  }
  const approval = record.approval;
  const zone = record.organization.timezone;
  const decidedBy =
    approval.decidedBy ??
    (approval.decidedByKind === "PLATFORM_ADMIN" ? L("ADM_APR_DECIDER_PLATFORM") : approval.decidedAt ? "—" : null);
  const facts = [
    {
      label: L("ADM_EMP_COL_EMPLOYEE"),
      value: approval.job ? `${approval.employee} · ${approval.job}` : approval.employee,
    },
    { label: L("ADM_APR_COL_INTENT"), value: L(`${intentKey(approval.intent)}_LONG`) },
    { label: L("ADM_APR_POLICY"), value: policyText(L, approval.policy) },
    { label: L("ADM_APR_COL_CREATED"), value: whenIn(L, locale, approval.createdAt, zone) },
    {
      label: L("ADM_APR_DECIDED"),
      value: approval.decidedAt
        ? [whenIn(L, locale, approval.decidedAt, zone), decidedBy].filter(Boolean).join(" · ")
        : "—",
    },
    { label: L("ADM_APR_RESULT"), value: resultText(L, approval) },
  ];
  const buttons = record.canDecide ? (
    <>
      <button type="button" className="tm-btn secondary" onClick={() => setDeciding("DENIED")}>
        {L("ADM_APR_REJECT")}
      </button>
      <button type="button" className="tm-btn primary" onClick={() => setDeciding("APPROVED")}>
        {L("ADM_APR_APPROVE")}
      </button>
    </>
  ) : null;
  return (
    <>
      <div className={own.ficha}>
        <PageHead
          title={approval.action}
          back={back}
          titleTag={<StatusTag status={approval.status} />}
          sub={formatText(L("ADM_APR_SUB"), approval.employee, whenIn(L, locale, approval.createdAt, zone, true))}
        >
          {buttons ? <span className={ui.desktopOnly}>{buttons}</span> : null}
        </PageHead>
      </div>
      {approval.status === "PENDING" && !record.canDecideAny ? (
        <Notice tone="info">{L("ADM_APR_CANNOT_DECIDE")}</Notice>
      ) : null}
      <div className={own.facts}>
        <Facts items={facts} />
      </div>
      <section className={ui.section} aria-labelledby="apr-args">
        <h2 id="apr-args" className={ui.subtitle} style={{ marginBottom: 12 }}>
          {L("ADM_APR_WHAT")}
        </h2>
        {record.arguments.length === 0 ? (
          <p className={ui.sectionLine}>{L("ADM_APR_NO_ARGUMENTS")}</p>
        ) : (
          <Facts items={record.arguments.map((item) => ({ label: argumentLabel(L, item.key), value: item.value }))} />
        )}
      </section>
      {buttons ? <MobileActions>{buttons}</MobileActions> : null}
      <ConfirmWindow
        open={!!deciding}
        danger={deciding === "DENIED"}
        title={formatText(
          L(deciding === "APPROVED" ? "ADM_APR_APPROVE_TITLE" : "ADM_APR_REJECT_TITLE"),
          approval.action,
        )}
        line={formatText(
          L(deciding === "APPROVED" ? "ADM_APR_APPROVE_LINE" : "ADM_APR_REJECT_LINE"),
          approval.employee,
        )}
        action={L(deciding === "APPROVED" ? "ADM_APR_APPROVE" : "ADM_APR_REJECT")}
        onClose={() => setDeciding(null)}
        onConfirm={async () => {
          const reply = await postJson<{ saved?: boolean; resumed?: boolean; messageKey?: string }>(
            `/api/admin/organizations/${orgId}/approvals/${approvalId}/decision`,
            { decision: deciding },
          );
          if (reply.status === 200 && reply.body?.saved) {
            toast.show(
              deciding === "APPROVED"
                ? formatText(L(reply.body.resumed ? "ADM_APR_APPROVED_RESUMED" : "ADM_APR_APPROVED"), approval.employee)
                : L("ADM_APR_REJECTED"),
            );
            void data.reload();
            return true;
          }
          toast.show(replyMessage(L, reply.body));
          void data.reload();
          return true;
        }}
      />
    </>
  );
}

type PolicyRow = {
  code: string;
  name: string;
  catalog: string;
  effective: string;
  hardened: boolean;
  company: string | null;
};

/**
 * `emp-politicas-aprob` · cada acción con la política del catálogo y la de esta empresa. Solo se puede endurecer: «Exigir
 * siempre» aparece mientras no sea la más exigente y «Restablecer» vuelve a la del catálogo; los dos con «Deshacer».
 */
export function ApprovalPoliciesPage({ orgId }: { orgId: number }) {
  const { L } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const data = useAiData<{ organization: OrgHead; items: PolicyRow[]; total: number }>(
    `/api/admin/organizations/${orgId}/approval-policies`,
  );
  useOpenOrg(data.data?.organization ?? null);
  useTitle(`${L("ADM_ORG_EMPLOYEES")} · ${L("ADM_EMP_TAB_POLICIES")}`);
  if (data.state === "missing") return <OrgNotFound />;
  const head = <EmployeesHead orgId={orgId} current="policies" sub={L("ADM_APR_POLICIES_LINE")} />;
  const list = data.data;
  if (!list) {
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        {head}
        <RowsSkeleton rows={4} table={!mobile} />
      </>
    );
  }
  const apply = async (row: PolicyRow, policy: string | null, undo: string | null | undefined) => {
    const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/approval-policies/${encodeURIComponent(row.code)}`,
      { policy },
    );
    if (reply.status !== 200 || !reply.body?.saved) {
      toast.show(replyMessage(L, reply.body));
      return;
    }
    void data.reload();
    toast.show(
      formatText(L(policy ? "ADM_APR_POLICY_HARDENED" : "ADM_APR_POLICY_RESET"), row.name),
      undo === undefined ? undefined : { label: L("ADM_UNDO"), run: () => void apply(row, undo, undefined) },
    );
  };
  // Como hoy: «Exigir siempre» mientras no sea la más exigente y «Restablecer» si la empresa la endureció.
  const action = (row: PolicyRow) =>
    policyRank(row.effective) < 2 || row.hardened ? (
      <>
        {policyRank(row.effective) < 2 ? (
          <button
            type="button"
            className={own.light}
            aria-label={formatText(L("ADM_APR_REQUIRE_FOR"), row.name)}
            onClick={() => void apply(row, "APPROVE_ALWAYS", row.company)}
          >
            {L("ADM_APR_REQUIRE")}
          </button>
        ) : null}
        {row.hardened ? (
          <button
            type="button"
            className={own.light}
            aria-label={formatText(L("ADM_APR_RESET_FOR"), row.name)}
            onClick={() => void apply(row, null, row.company)}
          >
            {L("ADM_APR_RESET")}
          </button>
        ) : null}
      </>
    ) : null;
  const effective = (row: PolicyRow) => (
    <span className={own.rowTitle} style={{ fontWeight: 400, fontSize: 14 }}>
      <span>{policyText(L, row.effective)}</span>
      {row.hardened ? <span className="tm-tag accent">{L("ADM_APR_HARDENED")}</span> : null}
    </span>
  );
  return (
    <>
      {head}
      {list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_APR_POLICIES_EMPTY")}</strong>
          <span>{L("ADM_APR_POLICIES_EMPTY_LINE")}</span>
          <a className="tm-btn secondary" href={`/Organizations/Empleados?id=${orgId}`}>
            {L("ADM_EMP_BACK")}
          </a>
        </div>
      ) : (
        <SimpleTable
          label={L("ADM_EMP_TAB_POLICIES")}
          rows={list.items}
          rowKey={(row) => row.code}
          columns={[
            { key: "name", title: L("ADM_EMP_COL_ACTION"), className: own.titleCell, render: (row) => row.name },
            { key: "catalog", title: L("ADM_APR_COL_CATALOG"), render: (row) => policyText(L, row.catalog) },
            { key: "company", title: L("ADM_APR_COL_COMPANY"), render: effective },
            { key: "action", title: "", className: own.endCell, render: action },
          ]}
          mobile={(row) => ({
            title: row.name,
            tag: row.hardened ? <span className="tm-tag accent">{L("ADM_APR_HARDENED")}</span> : undefined,
            line: formatText(L("ADM_APR_POLICY_MOBILE"), policyText(L, row.catalog), policyText(L, row.effective)),
            end: action(row),
          })}
        />
      )}
    </>
  );
}
